import FormPage from '../../components/ui/FormPage.tsx';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../../services/api.ts';
import { handlePtBrSubmit, ptBrValidityProps } from '../../utils/htmlValidation.ts';
import { formatPlate } from '../../utils/licensePlate.ts';
import DocumentUploadZone, {
  ExistingDocFile,
} from '../../components/frota/DocumentUploadZone.tsx';
import { DOC_STATUSES, DOC_TYPES } from '../../utils/fleetDocuments.ts';

type FormState = {
  docType: string;
  status: string;
  name: string;
  policyNumber: string;
  expiresAt: string;
  notes: string;
};

const emptyForm: FormState = {
  docType: 'seguro',
  status: 'ok',
  name: '',
  policyNumber: '',
  expiresAt: '',
  notes: '',
};

const FrotaDocumentoForm: React.FC = () => {
  const { id, docId } = useParams<{ id: string; docId?: string }>();
  const isEdit = Boolean(docId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [form, setForm] = useState<FormState>(emptyForm);
  const [file, setFile] = useState<File | null>(null);
  const [existingFile, setExistingFile] = useState<ExistingDocFile | null>(null);
  const [clearExisting, setClearExisting] = useState(false);

  const { data: vehicle, isLoading: loadingVehicle } = useQuery(
    ['fleet-vehicle', id],
    async () => (await api.get(`/patrimonio/frota/${id}`)).data,
    { enabled: !!id }
  );

  const { data: doc, isLoading: loadingDoc } = useQuery(
    ['fleet-document', id, docId],
    async () => (await api.get(`/patrimonio/frota/${id}/documentos/${docId}`)).data,
    { enabled: isEdit && !!id && !!docId }
  );

  useEffect(() => {
    if (!doc) return;
    setForm({
      docType: doc.docType === 'other' ? 'outro' : doc.docType || 'outro',
      status: doc.status || 'ok',
      name: doc.name || '',
      policyNumber: doc.policyNumber || '',
      expiresAt: doc.expiresAt || '',
      notes: doc.notes || '',
    });
    if (doc.fileUrl) {
      setExistingFile({
        fileUrl: doc.fileUrl,
        fileName: doc.fileName || 'arquivo',
        fileSize: doc.fileSize,
        fileMime: doc.fileMime,
      });
      setClearExisting(false);
    } else {
      setExistingFile(null);
    }
  }, [doc]);

  const uploadFileIfNeeded = async (): Promise<{
    fileUrl?: string | null;
    fileName?: string | null;
    fileSize?: number | null;
    fileMime?: string | null;
    clearFile?: boolean;
  }> => {
    if (file) {
      const formData = new FormData();
      formData.append('files', file);
      const uploadResponse = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!uploadResponse.data?.success) {
        throw new Error(uploadResponse.data?.error || 'Falha no upload do arquivo');
      }
      const uploaded = uploadResponse.data.data.files[0];
      return {
        fileUrl: uploaded.url,
        fileName: uploaded.originalName || uploaded.originalname || uploaded.filename || file.name,
        fileSize: uploaded.size ?? file.size,
        fileMime: uploaded.mimetype || file.type,
      };
    }
    if (isEdit && clearExisting) {
      return { clearFile: true };
    }
    return {};
  };

  const mutation = useMutation(
    async () => {
      const filePayload = await uploadFileIfNeeded();
      const payload: any = {
        docType: form.docType,
        status: form.status,
        name: form.name.trim(),
        policyNumber: form.policyNumber.trim() || null,
        expiresAt: form.expiresAt || null,
        notes: form.notes.trim() || null,
        ...filePayload,
      };
      if (isEdit) {
        return (await api.put(`/patrimonio/frota/${id}/documentos/${docId}`, payload)).data;
      }
      return (await api.post(`/patrimonio/frota/${id}/documentos`, payload)).data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['fleet-vehicle', id]);
        queryClient.invalidateQueries(['fleet']);
        if (docId) queryClient.invalidateQueries(['fleet-document', id, docId]);
        navigate(`/patrimonio/frota/${id}/documentos`);
      },
      onError: (err: any) =>
        setError(
          err?.response?.data?.error ||
            err?.message ||
            (isEdit ? 'Erro ao salvar alterações' : 'Erro ao salvar documento')
        ),
    }
  );

  const set = (k: keyof FormState, v: string) => setForm(prev => ({ ...prev, [k]: v }));

  if (loadingVehicle || (isEdit && loadingDoc)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  const plate = vehicle ? formatPlate(vehicle.licensePlate) : '';
  const vehicleLabel = vehicle
    ? `${vehicle.make || ''} ${vehicle.model || ''} · ${plate}`.trim()
    : '';

  return (
    <FormPage
      backTo={`/patrimonio/frota/${id}/documentos`}
      backLabel="Voltar aos documentos"
      breadcrumb={`Patrimônio / Frota / ${plate || '…'} / Documentos / ${isEdit ? 'Editar' : 'Novo'}`}
      title={isEdit ? 'Editar documento' : 'Adicionar documento'}
      subtitle={
        isEdit
          ? `Veículo: ${vehicleLabel}${form.name.trim() ? ` · ${form.name.trim()}` : ''}`
          : `Veículo: ${vehicleLabel}`
      }
      hint={
        isEdit
          ? 'Mesmo shell do cadastro, campos pré-preenchidos. Troque o arquivo sem perder os metadados.'
          : 'Arquivo opcional — você pode cadastrar o vencimento agora e anexar depois.'
      }
    >
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mb-4">
          {error}
        </div>
      )}

      <form
        className="space-y-5"
        noValidate
        onSubmit={e =>
          handlePtBrSubmit(e, () => {
            if (!form.name.trim()) {
              setError('Nome / descrição é obrigatório');
              return;
            }
            if (!form.docType) {
              setError('Tipo é obrigatório');
              return;
            }
            if (!form.status) {
              setError('Status é obrigatório');
              return;
            }
            setError('');
            mutation.mutate();
          })
        }
      >
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Dados do documento</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="text-sm">
              <span className="text-gray-600 block mb-1">Tipo *</span>
              <select
                required
                value={form.docType}
                onChange={e => set('docType', e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-3 py-2"
              >
                {DOC_TYPES.map(t => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="text-gray-600 block mb-1">Status *</span>
              <select
                required
                value={form.status}
                onChange={e => set('status', e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-3 py-2"
              >
                {DOC_STATUSES.map(s => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm sm:col-span-2">
              <span className="text-gray-600 block mb-1">Nome / descrição *</span>
              <input
                required
                {...ptBrValidityProps}
                value={form.name}
                onChange={e => set('name', e.target.value)}
                placeholder="Ex.: Seguro Porto Seguro"
                className="w-full border border-gray-300 rounded-xl px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="text-gray-600 block mb-1">Número (apólice etc.)</span>
              <input
                value={form.policyNumber}
                onChange={e => set('policyNumber', e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-3 py-2"
              />
              <span className="text-xs text-gray-400 mt-1 block">Opcional</span>
            </label>
            <label className="text-sm">
              <span className="text-gray-600 block mb-1">Vencimento</span>
              <input
                type="date"
                value={form.expiresAt}
                onChange={e => set('expiresAt', e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-3 py-2"
              />
              <span className="text-xs text-gray-400 mt-1 block">Opcional para CRLV sem data</span>
            </label>
            <label className="text-sm sm:col-span-2">
              <span className="text-gray-600 block mb-1">Observações</span>
              <textarea
                value={form.notes}
                onChange={e => set('notes', e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-xl px-3 py-2"
              />
            </label>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Arquivo</h3>
          <DocumentUploadZone
            file={file}
            existing={clearExisting ? null : existingFile}
            disabled={mutation.isLoading}
            onFileChange={next => {
              setFile(next);
              if (next) setClearExisting(false);
            }}
            onClearExisting={() => {
              setExistingFile(null);
              setClearExisting(true);
              setFile(null);
            }}
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(`/patrimonio/frota/${id}/documentos`)}
            className="btn btn-secondary"
            disabled={mutation.isLoading}
          >
            Cancelar
          </button>
          <button type="submit" disabled={mutation.isLoading} className="btn btn-primary">
            {mutation.isLoading
              ? 'Salvando…'
              : isEdit
                ? 'Salvar alterações'
                : 'Salvar documento'}
          </button>
        </div>
      </form>
    </FormPage>
  );
};

export default FrotaDocumentoForm;
